<template>
  <div
    class="w-full px-4 py-2 flex tablet-h:rounded-[10px] tablet-h:p-1.5 tablet-h:pr-2.5"
    :class="classes"
    @click="handleRowClick"
  >
    <div class="relative mr-[9px] size-11 shrink-0 p-0.5">
      <div class="size-full rounded-[4px] bg-gray-200/30 p-0.5">
        <Thumbnail
          :type="fileTypeToThumbnail(file?.type)"
          :src="
            applyAuthorizationToken(notification.primaryIcon.imageUrl) ??
            undefined
          "
        />
      </div>
      <Avatar
        v-if="notification.secondaryIcon?.imageUrl"
        class="absolute bottom-0 right-0 z-2"
        :size="18"
        :src="notification.secondaryIcon.imageUrl"
      />
    </div>
    <div
      class="text-[14px] leading-[19px] text-gray-800 -mt-1 flex flex-col justify-between"
    >
      <component :is="copy" class="mb-[3px]" />
      <div class="text-[12px] leading-[18px] opacity-60">
        {{ formatDistanceToNowStrict(props.notification.timestamp) }} ago
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  NotificationType,
  type File,
  type FileMetadata,
  type InviteMetadata,
  type Notification,
} from '@/shared/graphql/legacy/generated'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import { Avatar, Thumbnail } from '@dacr/library'
import { formatDistanceToNowStrict } from 'date-fns'
import { computed, h } from 'vue'
import { useRouter } from 'vue-router'

const props = defineProps<{
  notification: Notification
}>()

const router = useRouter()

const metadata = computed(() => props.notification.metadata as FileMetadata)

const inviteMetadata = computed(
  () => props.notification.metadata as InviteMetadata
)

const file = computed(() => metadata.value.file as File)

const isFileNotification = computed(
  () =>
    props.notification.type !== NotificationType.TeamMemberAccessRemoved &&
    !!file.value?.copyrightId
)

const classes = computed(() => ({
  'tablet-h:hover:bg-[#FAFAFA]': props.notification.read,
  'bg-green-700/10': !props.notification.read,
  'cursor-pointer': isFileNotification.value,
}))

const handleRowClick = () => {
  if (!isFileNotification.value) return

  router.push(`/file/${file.value.copyrightId}`)
}

const copy = computed(() => {
  if (props.notification.type === NotificationType.FileOwnerAccessRemoved) {
    return h('div', [
      `You have been removed as one of the owners for the copright of `,
      h('span', { class: 'font-medium break-all' }, file.value.fileName),
      '.',
    ])
  }

  if (props.notification.type === NotificationType.FileAuthorAccessRemoved) {
    return h('div', [
      `You have been removed as one of the authors for the copright of `,
      h('span', { class: 'font-medium break-all' }, file.value.fileName),
      '.',
    ])
  }

  if (
    props.notification.type === NotificationType.TeamMemberAccessRemoved &&
    inviteMetadata.value.invite.reference.__typename === 'Origin'
  ) {
    return h('div', [
      `You have been removed as one of the ${inviteMetadata.value.invite.reference.name} team members.`,
    ])
  }

  return null
})
</script>
